// Upload one prepared JPEG blob to a visit: presign → PUT to R2 → record (#107).
// The PUT goes browser→R2 directly, so R2 must allow PUT from this origin (a
// Cloudflare/R2 CORS config, already provisioned for #14). Shared by the
// signed-in Log-a-Visit flow (ReviewForm) and the guest sign-in merge
// (mergeGuestDrafts), so both paths upload identically.
// `visitDishId` (#115) attributes the photo to a specific dish on the visit, so
// it can show on that dish's review and be eligible for public sharing. Omit it
// for an un-attributed visit shot (receipt/storefront/table) — those stay private.
export async function uploadVisitBlob(apiBaseUrl, token, visitId, blob, visitDishId = null) {
  const presignRes = await fetch(`${apiBaseUrl}/photos/presign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ visit_id: visitId, content_type: 'image/jpeg', ext: 'jpg' }),
  });
  if (!presignRes.ok) throw new Error('photo upload failed');
  const { upload_url, r2_key } = await presignRes.json();

  const putRes = await fetch(upload_url, {
    method: 'PUT',
    headers: { 'Content-Type': 'image/jpeg' },
    body: blob,
  });
  if (!putRes.ok) throw new Error('photo upload failed');

  const record = { visit_id: visitId, r2_key, content_type: 'image/jpeg' };
  if (visitDishId != null) record.visit_dish_id = visitDishId;
  const recordRes = await fetch(`${apiBaseUrl}/photos/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(record),
  });
  if (!recordRes.ok) throw new Error('photo upload failed');
}

// Grouping a visit's photos by the dishes they show (#115, extended by #198).
//
// Extracted because this logic existed once, in UserProfilePage.js, and the
// own-profile view — a near-identically-named UserProfile.js — never got it.
// That is #230: correct code, wrong file, and no route by which the author of a
// visit could see their own multi-dish photos. A second hand-written copy would
// have set up the third. The layout around these photos legitimately differs per
// surface (own-profile opens the edit modal, public profile opens the photo), so
// what is shared is the LOGIC, not the markup.

/**
 * Split a visit's photos into per-dish groups plus the un-attributed remainder.
 *
 * A photo can name SEVERAL dishes — one table shot of quiche, toast and coffee is
 * a single upload, not three — so it appears in each of their groups, to be framed
 * to that dish by its own crop at render time.
 *
 * ⚠️ Reading the legacy scalar `visit_dish_id` on the photo instead of the
 * `dishes` array would put such a photo under ONE dish only: a wrong answer
 * rather than a missing one, which is the failure shape that survives review.
 *
 * A photo with no attributions at all is an un-attributed visit shot — a receipt,
 * a storefront, the table — and belongs in `loosePhotos`, not silently dropped.
 *
 * @param photos  the visit's photos
 * @param dishes  OPTIONAL. When given, a photo whose every attribution names a
 *   dish that isn't in this list also falls into `loosePhotos`. Callers render by
 *   iterating their dishes, so without this such a photo would appear in NO row
 *   and vanish from the page entirely — worse than showing it unframed, and on
 *   the own-profile view it would also remove the only way to click through and
 *   delete it. Omit to keep the plain grouping.
 * @returns {{photosByDish: Record<number, object[]>, loosePhotos: object[]}}
 */
export function groupPhotosByDish(photos, dishes) {
  const known = dishes ? new Set(dishes.map((d) => d.id)) : null;
  const photosByDish = {};
  const loosePhotos = [];
  (photos || []).forEach((p) => {
    const attributions = (p.dishes || []).filter(
      (a) => known === null || known.has(a.visit_dish_id)
    );
    if (attributions.length > 0) {
      attributions.forEach((a) => {
        (photosByDish[a.visit_dish_id] = photosByDish[a.visit_dish_id] || []).push(p);
      });
    } else {
      loosePhotos.push(p);
    }
  });
  return { photosByDish, loosePhotos };
}

/**
 * What to call a dish logged on a visit.
 *
 * #119: the typed display name (`custom_name`) wins over the linked catalog food —
 * someone who wrote "Ube Cruffin" should see "Ube Cruffin", not the catalog's
 * tidied-up name for whatever it matched.
 */
export function visitDishLabel(dish) {
  if (!dish) return 'Dish';
  return dish.custom_name || dish.menu_item_name || dish.food_name || dish.dish_name || 'Dish';
}
